import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import type {
  Booking,
  ChatMessage,
  Conversation,
  CouponOption,
  Company,
  DashboardSummary,
  DiscountRedemption,
  DiscountRule,
  Guardian,
  InventoryItem,
  MessageTemplate,
  Organization,
  Product,
  Quote,
  Receivable,
  ReceivablesSummary,
  Room,
  SessionsResponse,
  Shift,
  ShiftReport,
} from "../lib/types";

/** Centralised query keys so mutations can invalidate precisely. */
export const keys = {
  sessions: ["sessions"] as const,
  catalog: ["catalog"] as const,
  shift: ["shift"] as const,
  conversations: ["conversations"] as const,
  messages: (guardianId: string) => ["messages", guardianId] as const,
  templates: ["templates"] as const,
  bookings: ["bookings"] as const,
  rooms: ["rooms"] as const,
  organizations: ["organizations"] as const,
  receivables: ["receivables"] as const,
  inventory: ["inventory"] as const,
  dashboard: ["dashboard"] as const,
  bands: ["bands"] as const,
  discountRules: ["discount-rules"] as const,
  company: ["company"] as const,
  discountRedemptions: ["discount-redemptions"] as const,
  search: (q: string) => ["search", q] as const,
};

/** Invalidate everything a money-moving action can change. */
export function useRefreshAll() {
  const qc = useQueryClient();
  return () => {
    for (const k of [keys.sessions, keys.shift, keys.inventory, keys.dashboard, keys.bookings, keys.receivables, keys.bands, keys.conversations]) {
      void qc.invalidateQueries({ queryKey: k });
    }
  };
}

export const useSessions = () =>
  useQuery({ queryKey: keys.sessions, queryFn: () => api.get<SessionsResponse>("/sessions"), refetchInterval: 10_000 });

/** Stamps for a free visit, as the owner set them (0 = no loyalty programme). */
export const useStampsNeeded = (): number => useSessions().data?.rules.stampsNeeded ?? 6;

/** `birthDate` (v2) prices the play-time tickets for that child's age group; ignored by v1. */
export const useCatalog = (birthDate?: string) =>
  useQuery({
    queryKey: birthDate ? [...keys.catalog, birthDate] : keys.catalog,
    queryFn: async () => (await api.get<{ products: Product[] }>(`/catalog${birthDate ? `?birthDate=${birthDate}` : ""}`)).products,
    staleTime: 60_000,
    placeholderData: keepPreviousData,
  });

export const useShift = () =>
  useQuery({
    queryKey: keys.shift,
    queryFn: () => api.get<{ shift: Shift | null; report: ShiftReport | null }>("/shifts/current"),
    refetchInterval: 30_000,
  });

export const useAvailableBands = (enabled = true) =>
  useQuery({ queryKey: keys.bands, enabled, queryFn: async () => (await api.get<{ bands: string[] }>("/sessions/bands/available?limit=20")).bands });

export const useCustomerSearch = (q: string) =>
  useQuery({
    queryKey: keys.search(q),
    enabled: q.trim().length >= 3,
    placeholderData: keepPreviousData,
    queryFn: async () => (await api.get<{ guardians: Guardian[] }>(`/customers/search?q=${encodeURIComponent(q.trim())}`)).guardians,
  });

export const useConversations = () =>
  useQuery({ queryKey: keys.conversations, queryFn: async () => (await api.get<{ conversations: Conversation[] }>("/whatsapp/conversations")).conversations, refetchInterval: 5000 });

export const useMessages = (guardianId: string | null) =>
  useQuery({
    queryKey: keys.messages(guardianId ?? ""),
    enabled: !!guardianId,
    queryFn: async () => (await api.get<{ messages: ChatMessage[] }>(`/whatsapp/conversations/${guardianId}/messages`)).messages,
    refetchInterval: 3000,
  });

export const useTemplates = () =>
  useQuery({ queryKey: keys.templates, queryFn: async () => (await api.get<{ templates: MessageTemplate[] }>("/whatsapp/templates")).templates });

export const useBookings = () =>
  useQuery({ queryKey: keys.bookings, queryFn: async () => (await api.get<{ bookings: Booking[] }>("/bookings")).bookings, refetchInterval: 30_000 });

export const useRooms = () =>
  useQuery({ queryKey: keys.rooms, queryFn: async () => (await api.get<{ rooms: Room[] }>("/bookings/rooms")).rooms, staleTime: 300_000 });

export const useOrganizations = () =>
  useQuery({ queryKey: keys.organizations, queryFn: async () => (await api.get<{ organizations: Organization[] }>("/customers/organizations")).organizations });

export const useReceivables = (enabled = true) =>
  useQuery({
    queryKey: keys.receivables,
    enabled,
    queryFn: () => api.get<{ receivables: Receivable[]; summary: ReceivablesSummary }>("/receivables"),
  });

export const useInventory = () =>
  useQuery({ queryKey: keys.inventory, queryFn: async () => (await api.get<{ items: InventoryItem[] }>("/inventory")).items });

export const useDashboard = (enabled = true) =>
  useQuery({ queryKey: keys.dashboard, enabled, queryFn: () => api.get<DashboardSummary>("/dashboard/summary"), refetchInterval: 30_000 });

export async function fetchQuote(body: { type: string; packageProductId: string; kidsCount: number; addons: Array<{ productId: string; qty: number }> }): Promise<Quote> {
  return (await api.post<{ quote: Quote }>("/bookings/quote", body)).quote;
}

export const useDiscountRules = (all = false) =>
  useQuery({
    queryKey: [...keys.discountRules, all],
    queryFn: async () => (await api.get<{ rules: DiscountRule[] }>(`/discounts/rules${all ? "?all=true" : ""}`)).rules,
  });

export const useDiscountRedemptions = () =>
  useQuery({
    queryKey: keys.discountRedemptions,
    queryFn: () => api.get<{ redemptions: DiscountRedemption[]; today: { total: number; count: number } }>("/discounts/redemptions"),
  });

/** Which coupons apply to this customer + cart, and for how much (the server decides). */
export const useCouponEvaluation = (input: { guardianId: string | null; kidId: string | null; passProductId: string; addons: Array<{ productId: string; qty: number }>; /** post-paid: price the coupon on this visit's running bill */ sessionId?: string }) =>
  useQuery({
    queryKey: ["coupons", input],
    enabled: !!input.passProductId,
    placeholderData: keepPreviousData,
    queryFn: () => api.post<{ options: CouponOption[] }>("/discounts/evaluate", input),
  });

export const useCompany = () =>
  useQuery({ queryKey: keys.company, queryFn: async () => (await api.get<{ company: Company }>("/settings/company")).company, staleTime: 60_000 });
